import { useState, useEffect, useMemo, useCallback } from 'react';
import { useTheme } from '../../themes/ThemeProvider';
import { Star, Share2, User, ChevronUp, ChevronDown, Calendar, MapPin, Phone, Mail, Flag } from 'lucide-react';
import HeaderContainer from '../core/HeadersAndFooters/HeaderContainer';
import HeaderButtonsDetail from '../core/HeadersAndFooters/HeaderButtonsDetail';
import FooterContainer from '../core/HeadersAndFooters/FooterContainer';
import FooterButtonsDetail from '../core/HeadersAndFooters/FooterButtonsDetail';
import ConfirmationModal from '../core/ConfirmationModal';
import ShareLinkModal from '../core/ShareLinkModal';
import ReportModal from '../shared/ReportModal';
import ConcertinaThread from '../chat/ConcertinaThread';
import useMediaQuery from '../../hooks/useMediaQuery';
import useCommentBarLayout from '../../hooks/useCommentBarLayout';
import { chatService, userActionService } from '../../data/dataService';
import { supabaseFetch } from '../../lib/supabase';
import { useTranslation } from '../../i18n/useTranslation';
import { formatTextWithStyles } from '../../utils/formatText';
import { parseEventDate } from '../../utils/eventDateTime';
import { useTagHouseFallback } from '../../hooks/useTagHouseFallback';

// Synchronous last-resort placeholder — used only until useTagHouseFallback()
// resolves (or if that fetch fails). Replaces the previous '/images/default-event.jpg'
// reference, which pointed at a file that does not exist in /public/ (a live,
// pre-existing broken-image defect, fixed as part of the image-layer build).
const FALLBACK_IMAGE = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='400' height='300' fill='%23e5e7eb'%3E%3Crect width='400' height='300'/%3E%3C/svg%3E";

// Collect all flat descendants of a root comment (engine's loadResponses shape)
const collectDescendants = (rootId, all) => {
  const out = [];
  for (const d of all.filter(c => c.parent_chat_id === rootId)) {
    out.push(d);
    out.push(...collectDescendants(d.id, all));
  }
  return out;
};

const EventsDetail = ({ event, onBack, onViewGroup, context, onGroupHome, userId }) => {
  const { isMobile, sidebarWidth, isSidebarCompact, headerHeight, footerHeight } = useMediaQuery('detail');
  const theme = useTheme();
  const { t, formattingLocale } = useTranslation('detail');
  // R4 house fallback (image-layer build) — null until fetched.
  const houseFallback = useTagHouseFallback();

  // Rating state
  const [userRating, setUserRating] = useState(null);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [viewCount, setViewCount] = useState(0);

  // Modal state
  const [modal, setModal] = useState({ visible: false, message: '', onOk: null });
  const [showShareModal, setShowShareModal] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);

  // Mobile comments panel state
  const [commentsExpanded, setCommentsExpanded] = useState(false);
  const { pageBottomPadding, scrollHeight } = useCommentBarLayout(commentsExpanded);

  // Comment system state (engine-owned rendering; consumer owns data + count)
  const [allComments, setAllComments] = useState([]);
  const [commentCount, setCommentCount] = useState(0);

  // Attendance state
  const [isAttending, setIsAttending] = useState(false);

  // Load attendance status
  useEffect(() => {
    const loadAttendance = async () => {
      if (!userId || !event?.id) return;
      try {
        const attending = await userActionService.isAttendingEvent(userId, event.id);
        setIsAttending(attending);
      } catch (error) {
        console.error('Error loading attendance status:', error);
      }
    };
    loadAttendance();
  }, [userId, event]);

  // Join/Leave event handler
  const handleJoinLeaveEvent = async () => {
    if (!userId) {
      setModal({
        visible: true,
        message: t('events:please_log_in_to_manage_attendance'),
        onOk: () => setModal({ visible: false, message: '', onOk: null })
      });
      return;
    }

    if (isAttending) {
      const result = await userActionService.leaveEvent(userId, event.id);
      if (result === true) {
        setIsAttending(false);
        setModal({
          visible: true,
          message: t('events:you_have_left_event', { eventTitle: event.title }),
          onOk: () => setModal({ visible: false, message: '', onOk: null })
        });
      } else {
        setModal({
          visible: true,
          message: t('events:failed_to_leave_event'),
          onOk: () => setModal({ visible: false, message: '', onOk: null })
        });
      }
    } else {
      const result = await userActionService.attendEvent(userId, event.id);
      if (result === true) {
        setIsAttending(true);
        setModal({
          visible: true,
          message: t('events:successfully_joined_event', { eventTitle: event.title }),
          onOk: () => setModal({ visible: false, message: '', onOk: null })
        });
      } else {
        setModal({
          visible: true,
          message: t('events:failed_to_join_event'),
          onOk: () => setModal({ visible: false, message: '', onOk: null })
        });
      }
    }
  };

  // Navigate to edit event
  const handleEditEvent = () => {
    window.dispatchEvent(new CustomEvent('ournet:navigate', {
      detail: { view: 'new-event', mode: 'edit', article: event }
    }));
  };

  const isAuthor = userId && event?.author_id === userId;

  // Logo click listener - navigate back to MainFeed
  useEffect(() => {
    const handleLogoClick = () => {
      if (onBack) onBack();
    };
    window.addEventListener('ournet:context-switch', handleLogoClick);
    return () => window.removeEventListener('ournet:context-switch', handleLogoClick);
  }, [onBack]);

  // Load user ID, rating, and like/dislike status when event loads
  useEffect(() => {
    const loadUserAndRating = async () => {
      if (!event?.id) {
        return;
      }

      try {
        // Load view count (runs for all visitors)
        const { data: viewData } = await supabaseFetch.query('article_engagement', {
          select: 'id',
          eq: { article_id: event.id, engagement_type: 'view' }
        });
        if (viewData) setViewCount(viewData.length);

        if (userId) {
          setCurrentUserId(userId);

          // Record view for reputation (fire-and-forget, non-blocking)
          userActionService.trackView(event.id, userId).catch(() => {});

          // Load user's existing rating for this event
          const rating = await userActionService.getEventRating(userId, event.id);
          setUserRating(rating);
        }
      } catch (error) {
        console.error('Error loading user rating:', error);
      }
    };

    loadUserAndRating();
  }, [event, userId]);

  // --- Comment data wiring (consumer owns data + count; engine owns rendering) ---
  // Re-fetch all comments as a FLAT list (events are articles — event.id is the article_id in chats)
  const reloadComments = useCallback(async () => {
    if (!event?.id) return;
    try {
      const data = await chatService.getArticleComments(event.id);
      setAllComments(data);
      setCommentCount(data.filter(c => !c.is_deleted_by_author).length);
    } catch (error) {
      console.error('Error loading event comments:', error);
    }
  }, [event?.id]);

  // Load comments when event loads
  useEffect(() => {
    reloadComments();
  }, [reloadComments]);

  // Top-level comments annotated with numeric (non-deleted) reply_count.
  const topLevelComments = useMemo(
    () => allComments
      .filter(c => !c.parent_chat_id)
      .map(c => ({
        ...c,
        reply_count: collectDescendants(c.id, allComments).filter(r => !r.is_deleted_by_author).length
      })),
    [allComments]
  );

  // Lazy responses loader: flat descendants of a root comment.
  const loadResponses = useCallback(
    (rootId) => Promise.resolve(collectDescendants(rootId, allComments)),
    [allComments]
  );

  // Create a top-level comment (engine calls onRefresh itself afterwards).
  const onCreateTopLevel = useCallback(async ({ content }) => {
    await chatService.createArticleComment({
      content,
      article_id: event.id,
      parent_chat_id: null,
      author_id: currentUserId
    });
    userActionService.trackComment(event.id, currentUserId).catch(() => {});
  }, [event.id, currentUserId]);

  // Create a reply (engine calls onRefresh itself afterwards).
  const onCreateReply = useCallback(async ({ parentChatId, content }) => {
    await chatService.createArticleComment({
      content,
      article_id: event.id,
      parent_chat_id: parentChatId,
      author_id: currentUserId
    });
  }, [event.id, currentUserId]);

  // Handle star rating click
  const handleStarClick = async (rating) => {
    if (!currentUserId) {
      console.warn('User must be logged in to rate events');
      setModal({
        visible: true,
        message: t('auth:login_to_rate'),
        onOk: () => setModal({ visible: false, message: '', onOk: null })
      });
      return;
    }

    try {
      const success = await userActionService.setEventRating(currentUserId, event.id, rating);

      if (success) {
        setUserRating(rating);
      } else {
        console.error('Failed to save rating');
        setModal({
          visible: true,
          message: t('failed_to_save_rating'),
          onOk: () => setModal({ visible: false, message: '', onOk: null })
        });
      }
    } catch (error) {
      console.error('Error saving rating:', error);
      setModal({
        visible: true,
        message: t('error_saving_rating_prefix') + ' ' + error.message,
        onOk: () => setModal({ visible: false, message: '', onOk: null })
      });
    }
  };

  if (!event) {
    return <div>{t('event_not_found')}</div>;
  }

  // Calculate attendee percentage for progress bar
  const attendeePercentage = event.max_attendees
    ? Math.min(100, ((event.current_attendees || 0) / event.max_attendees) * 100)
    : 0;

  // Mobile Layout
  if (isMobile) {
    return (
      <>
        {/* Header - Layered architecture */}
        <HeaderContainer />
        <HeaderButtonsDetail
          currentContext={{ name: event.group_name || event.title }}
          onBack={onBack}
        />

        <div style={{
          width: '100%',
          minHeight: '100vh',
          backgroundColor: '#F8FAFC',
          paddingTop: `${headerHeight}px`,
          paddingBottom: `${pageBottomPadding}px`
        }}>
        {/* Main Content - Scrollable */}
        <div style={{
          padding: '0',
          overflowY: 'auto',
          height: scrollHeight
        }}>
          {/* Event Image - 16:9 */}
          <div style={{
            width: '100%',
            aspectRatio: '16 / 9',
            position: 'relative',
            overflow: 'hidden'
          }}>
            <img
              src={event.cover_image_url || houseFallback || FALLBACK_IMAGE}
              alt={event.title}
              style={{
                width: '100%',
                height: '100%',
                // §5a — never crop the illustration's own border; contain +
                // this page's background (#F8FAFC, matching the ancestor
                // container two levels up) as the letterbox fill.
                objectFit: 'contain',
                backgroundColor: '#F8FAFC'
              }}
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = houseFallback || FALLBACK_IMAGE;
              }}
            />
          </div>

          {/* Content Section */}
          <div style={{ padding: '16px' }}>
            {/* Title */}
            <h1 style={{
              fontSize: '18px',
              fontWeight: '700',
              color: '#1E293B',
              marginBottom: '16px',
              lineHeight: '1.3'
            }}>
              {event.title}
            </h1>

            {/* Date/Time */}
            {event.start_date && (
              <div style={{
                display: 'flex',
                alignItems: 'flex-start',
                marginBottom: '12px',
                color: '#64748B',
                fontSize: '14px',
                gap: '8px'
              }}>
                <Calendar size={16} style={{ marginTop: '2px', flexShrink: 0 }} />
                <div>
                  {parseEventDate(event.start_date).toLocaleDateString(formattingLocale, {
                    weekday: 'long',
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                  <div style={{ fontSize: '13px' }}>
                    {parseEventDate(event.start_date).toLocaleTimeString(formattingLocale, {
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                    {event.end_date && (() => {
                      const endDateObj = parseEventDate(event.end_date);
                      const isMultiDay = endDateObj.toDateString() !== parseEventDate(event.start_date).toDateString();
                      // Multi-day: full weekday/date/time, matching the desktop range exactly
                      // (this component's own desktop branch, below) — a same-day end keeps the
                      // existing time-only line.
                      return isMultiDay
                        ? ` – ${endDateObj.toLocaleDateString(formattingLocale, {
                            weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
                            hour: '2-digit', minute: '2-digit'
                          })}`
                        : ` - ${endDateObj.toLocaleTimeString(formattingLocale, { hour: '2-digit', minute: '2-digit' })}`;
                    })()}
                  </div>
                </div>
              </div>
            )}

            {/* Location */}
            {event.venue_name && (
              <div style={{
                display: 'flex',
                alignItems: 'flex-start',
                marginBottom: '12px',
                color: '#64748B',
                fontSize: '14px',
                gap: '8px'
              }}>
                <MapPin size={16} style={{ marginTop: '2px', flexShrink: 0 }} />
                <div>
                  <div style={{ fontWeight: '500' }}>{event.venue_name}</div>
                  {event.venue_address && <div style={{ fontSize: '13px' }}>{event.venue_address}</div>}
                  {event.venue_postcode && <div style={{ fontSize: '13px' }}>{event.venue_postcode}</div>}
                </div>
              </div>
            )}

            {/* Organizer */}
            <div style={{ marginBottom: '12px' }}>
              <div style={{
                fontSize: '10px',
                color: '#6B7280',
                fontWeight: '500',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                marginBottom: '4px'
              }}>
                {t('event_owner')}
              </div>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                color: '#64748B',
                fontSize: '14px',
                gap: '8px'
              }}>
                <User size={16} />
                <span>{t('organized_by')}: <strong style={{ color: theme?.primary || '#024B99' }}>{event.author_name || t('unknown_author')}</strong></span>
              </div>
            </div>

            {/* Rate this Event */}
            <div style={{ marginBottom: '16px' }}>
              <div style={{
                fontSize: '13px',
                fontWeight: '600',
                color: '#1E293B',
                marginBottom: '8px'
              }}>
                {t('rate_this_event')}
              </div>
              <div style={{ display: 'flex', gap: '8px' }}>
                {[1, 2, 3, 4, 5].map((starNumber) => {
                  const isFilled = userRating !== null && userRating >= starNumber;
                  return (
                    <Star
                      key={starNumber}
                      size={28}
                      fill={isFilled ? (theme?.primary || '#024B99') : 'none'}
                      stroke={theme?.primary || '#024B99'}
                      strokeWidth={2}
                      style={{ cursor: 'pointer' }}
                      onClick={() => handleStarClick(starNumber)}
                    />
                  );
                })}
              </div>
            </div>

            {/* Attendees with Progress Bar */}
            {event.max_attendees && (
              <div style={{ marginBottom: '20px' }}>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  marginBottom: '6px',
                  fontSize: '13px',
                  color: '#64748B'
                }}>
                  <span>{t('attendees')}</span>
                  <span style={{ fontWeight: '600', color: '#1E293B' }}>
                    {event.current_attendees || 0} / {event.max_attendees}
                  </span>
                </div>
                <div style={{
                  width: '100%',
                  height: '8px',
                  backgroundColor: '#E2E8F0',
                  borderRadius: '4px',
                  overflow: 'hidden'
                }}>
                  <div style={{
                    width: `${attendeePercentage}%`,
                    height: '100%',
                    backgroundColor: theme?.primary || '#024B99',
                    borderRadius: '4px',
                    transition: 'width 0.3s ease'
                  }} />
                </div>
              </div>
            )}

            {/* Divider */}
            <div style={{ borderTop: '1px solid #E2E8F0', margin: '16px 0' }} />

            {/* Event Description */}
            {event.content && (
              <div style={{ marginBottom: '20px' }}>
                <h3 style={{
                  fontSize: '15px',
                  fontWeight: '600',
                  color: '#1E293B',
                  marginBottom: '8px'
                }}>
                  {t('about_this_event')}
                </h3>
                <div style={{
                  fontSize: '14px',
                  lineHeight: '1.6',
                  color: '#374151'
                }}>
                  {formatTextWithStyles(event.content, 'evt-mob-', { marginBottom: '12px' })}
                </div>
              </div>
            )}

            {/* Contact Information */}
            {(event.contact_phone || event.contact_email) && (
              <div style={{ marginBottom: '20px' }}>
                <h3 style={{
                  fontSize: '15px',
                  fontWeight: '600',
                  color: '#1E293B',
                  marginBottom: '8px'
                }}>
                  {t('contact_information')}
                </h3>

                {event.contact_phone && (
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    marginBottom: '8px',
                    color: '#64748B',
                    fontSize: '14px',
                    gap: '8px'
                  }}>
                    <Phone size={16} />
                    <a href={`tel:${event.contact_phone}`} style={{ color: theme?.primary || '#024B99' }}>
                      {event.contact_phone}
                    </a>
                  </div>
                )}

                {event.contact_email && (
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    color: '#64748B',
                    fontSize: '14px',
                    gap: '8px'
                  }}>
                    <Mail size={16} />
                    <a href={`mailto:${event.contact_email}`} style={{ color: theme?.primary || '#024B99' }}>
                      {event.contact_email}
                    </a>
                  </div>
                )}
              </div>
            )}

            {/* Join/Leave or Edit Event Button */}
            {isAuthor ? (
              <button
                onClick={handleEditEvent}
                style={{
                  width: '100%',
                  padding: '14px',
                  backgroundColor: theme?.primary || '#024B99',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '15px',
                  fontWeight: '700',
                  cursor: 'pointer',
                  marginBottom: '16px'
                }}
              >
                {t('events:edit_event')}
              </button>
            ) : (
              <button
                onClick={handleJoinLeaveEvent}
                style={{
                  width: '100%',
                  padding: '14px',
                  backgroundColor: isAttending ? '#dc2626' : (theme?.primary || '#024B99'),
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '15px',
                  fontWeight: '700',
                  cursor: 'pointer',
                  marginBottom: '16px'
                }}
              >
                {isAttending ? t('events:leave_event') : t('events:join_event')}
              </button>
            )}

            {/* Divider */}
            <div style={{ borderTop: '1px solid #E2E8F0', margin: '16px 0' }} />

            {/* Buttons Row - Share, Report, and See Their Other Events */}
            <div style={{ display: 'flex', gap: '10px', marginBottom: '20px' }}>
              {/* Share Button */}
              <button
                onClick={() => setShowShareModal(true)}
                style={{
                  flex: 1,
                  padding: '12px',
                  backgroundColor: theme?.primary || '#024B99',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '13px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '6px'
                }}
              >
                <Share2 size={16} />
                {t('share')}
              </button>

              {/* Report Button */}
              <button
                onClick={() => {
                  if (!currentUserId) {
                    setModal({
                      visible: true,
                      message: t('auth:login_required') || 'Please log in to report content',
                      onOk: () => setModal({ visible: false, message: '', onOk: null })
                    });
                    return;
                  }
                  setShowReportModal(true);
                }}
                style={{
                  padding: '12px',
                  backgroundColor: 'transparent',
                  color: theme?.error || '#DC2626',
                  border: `1px solid ${theme?.error || '#DC2626'}`,
                  borderRadius: '8px',
                  fontSize: '13px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '6px'
                }}
              >
                <Flag size={16} />
              </button>

              {/* See Their Other Events Button */}
              <button
                style={{
                  flex: 1,
                  padding: '12px',
                  backgroundColor: 'white',
                  color: theme?.primary || '#024B99',
                  border: `2px solid ${theme?.primary || '#024B99'}`,
                  borderRadius: '8px',
                  fontSize: '13px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                {t('see_their_other_events')}
              </button>
            </div>
          </div>
        </div>

        {/* Expandable Comments Panel */}
        {commentsExpanded ? (
          <div style={{
            position: 'fixed',
            bottom: '60px',
            left: 0,
            right: 0,
            height: '50vh',
            backgroundColor: 'white',
            borderTop: '1px solid #E5E7EB',
            borderRadius: '16px 16px 0 0',
            boxShadow: '0 -4px 20px rgba(0,0,0,0.1)',
            display: 'flex',
            flexDirection: 'column',
            zIndex: 100
          }}>
            <div style={{ padding: '12px 16px', borderBottom: '1px solid #E5E7EB', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
              <span style={{ fontWeight: '600', fontSize: '14px' }}>{t('comments')} ({commentCount})</span>
              <button onClick={() => setCommentsExpanded(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px' }}>
                <ChevronDown size={24} color="#6B7280" />
              </button>
            </div>
            <div style={{ flex: 1, overflowY: 'auto', padding: '12px 16px' }}>
              <ConcertinaThread
                variant="comment"
                data={topLevelComments}
                loadResponses={loadResponses}
                onCreateTopLevel={onCreateTopLevel}
                onCreateReply={onCreateReply}
                onRefresh={reloadComments}
                reportContentType="chat"
                emptyState={t('no_comments_yet')}
              />
            </div>
          </div>
        ) : (
          <div onClick={() => setCommentsExpanded(true)} style={{ position: 'fixed', bottom: '60px', left: 0, right: 0, backgroundColor: '#F8FAFC', borderTop: '1px solid #E5E7EB', cursor: 'pointer', zIndex: 100, paddingTop: '8px', paddingBottom: '8px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 16px 4px 16px' }}>
              <span style={{ fontWeight: '600', fontSize: '14px', color: '#1F2937' }}>{allComments.length} {t('comments')}</span>
              <ChevronUp size={20} color="#6B7280" />
            </div>
            {allComments.filter(c => !c.parent_chat_id)[0] && (
              <div style={{ padding: '0 16px', fontSize: '13px', color: '#6B7280', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontStyle: 'italic' }}>
                {allComments.filter(c => !c.parent_chat_id)[0]?.content}
              </div>
            )}
          </div>
        )}

        {/* Mobile Footer */}
        <FooterContainer viewType="detail" isMobile={true}>
          <FooterButtonsDetail />
        </FooterContainer>

        {/* Confirmation Modal */}
        <ConfirmationModal
          visible={modal.visible}
          title={t('notification')}
          message={modal.message}
          onOk={() => {
            try {
              modal.onOk && modal.onOk();
            } catch (e) {
              console.error(e);
              setModal({ visible: false, message: '', onOk: null });
            }
          }}
          onCancel={() => {
            setModal({ visible: false, message: '', onOk: null });
          }}
        />

        {/* Share Link Modal */}
        <ShareLinkModal
          visible={showShareModal}
          targetType="event"
          targetId={event.id}
          targetName={event.title}
          currentUser={userId ? { id: userId } : null}
          context={context}
          isPublic={event.is_public}
          communityId={event.community_id}
          onClose={() => setShowShareModal(false)}
        />
        </div>
      </>
    );
  }

  // Desktop Layout
  return (
    <div style={{
      width: '100vw',
      height: '100vh',
      backgroundColor: '#FFFFFF',
      overflow: 'hidden',
      position: 'relative'
    }}>
    {/* Header - Layered architecture */}
    <HeaderContainer />
    <HeaderButtonsDetail
      currentContext={{ name: event.title }}
      onBack={onBack}
    />

    {/* Main Content Container */}
    <div style={{
      position: 'absolute',
      top: `${headerHeight}px`,
      left: '0',
      right: '0',
      bottom: `${footerHeight}px`,
      display: 'flex'
    }}>
    {/* Left Sidebar - Dynamic width (384-480px based on viewport) */}
    <div style={{
      width: `${sidebarWidth}px`,
      minWidth: `${sidebarWidth}px`,
      height: '100%',
      backgroundColor: '#F8FAFC',
      borderRight: '2px solid #E2E8F0',
      display: 'flex',
      flexDirection: 'column',
      position: 'relative',
      transition: 'width 0.3s ease'
    }}>
    {/* Event Image */}
    <div style={{
      width: '100%',
      height: '240px',
      position: 'relative',
      overflow: 'hidden'
    }}>
    <img
    src={event.cover_image_url || houseFallback || FALLBACK_IMAGE}
    alt={event.title}
    style={{
      width: '100%',
      height: '100%',
      // §5a — never crop the illustration's own border; contain + this
      // sidebar's background (#F8FAFC, matching the container above) as the
      // letterbox fill.
      objectFit: 'contain',
      backgroundColor: '#F8FAFC'
    }}
    onError={(e) => {
      e.target.onerror = null;
                e.target.src = houseFallback || FALLBACK_IMAGE;
    }}
    />
    </div>

    {/* Event Details */}
    <div style={{
      padding: isSidebarCompact ? '16px' : '24px',
      flex: '1',
      overflowY: 'auto',
      transition: 'padding 0.3s ease'
    }}>
    <h2 style={{
      fontSize: '24px',
      fontWeight: '700',
      color: theme?.text || '#1E293B',
      marginBottom: '16px',
      fontFamily: 'OpenSauceSans, -apple-system, BlinkMacSystemFont, sans-serif'
    }}>
    {event.title}
    </h2>

    {/* Event Meta Information */}
    <div style={{ marginBottom: '24px' }}>
    {/* Event Dates */}
    {event.start_date && (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        marginBottom: '12px',
        color: theme?.textSecondary || '#64748B',
        fontSize: '14px'
      }}>
      <Calendar size={16} style={{ marginRight: '8px' }} />
      {parseEventDate(event.start_date).toLocaleDateString(formattingLocale, {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })}
      {event.end_date && ` - ${parseEventDate(event.end_date).toLocaleDateString(formattingLocale, {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })}`}
      </div>
    )}

    {/* Venue Information */}
    {event.venue_name && (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        marginBottom: '12px',
        color: theme?.textSecondary || '#64748B',
        fontSize: '14px'
      }}>
      <MapPin size={16} style={{ marginRight: '8px' }} />
      <div>
        <div>{event.venue_name}</div>
        {event.venue_address && <div style={{ fontSize: '12px' }}>{event.venue_address}</div>}
        {event.venue_postcode && <div style={{ fontSize: '12px' }}>{event.venue_postcode}</div>}
      </div>
      </div>
    )}

    {/* Author + Stats (two-column layout) */}
    <div style={{ marginBottom: '12px', display: 'flex', gap: '16px' }}>
      {/* Left: Author */}
      <div style={{ flex: 1 }}>
        <div style={{
          fontSize: '10px',
          color: '#6B7280',
          fontWeight: '500',
          textTransform: 'uppercase',
          letterSpacing: '0.5px',
          marginBottom: '2px'
        }}>
          {t('event_owner')}
        </div>
        <div style={{
          fontSize: '14px',
          fontWeight: '600',
          color: '#1F2937',
          marginBottom: '2px'
        }}>
          {event.author_name || t('unknown_author')}
        </div>
      </div>

      {/* Right: Stats */}
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: '11px', lineHeight: '1.5' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <span style={{ color: '#6B7280' }}>{t('views')}:</span>
            <span style={{ fontWeight: '600' }}>{viewCount}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <span style={{ color: '#6B7280' }}>{t('comments')}:</span>
            <span style={{ fontWeight: '600' }}>{commentCount}</span>
          </div>
        </div>
      </div>
    </div>

    {/* Rate this Event */}
    <div style={{ marginBottom: '12px' }}>
      <div style={{
        fontSize: '13px',
        fontWeight: '600',
        color: theme?.text || '#1E293B',
        marginBottom: '6px'
      }}>
        {t('rate_this_event')}
      </div>
      <div style={{ display: 'flex', gap: '4px' }}>
        {[1, 2, 3, 4, 5].map((starNumber) => {
          const isFilled = userRating !== null && userRating >= starNumber;
          return (
            <Star
              key={starNumber}
              size={20}
              fill={isFilled ? (theme?.primary || '#024B99') : 'none'}
              stroke={theme?.primary || '#024B99'}
              strokeWidth={2}
              style={{
                cursor: 'pointer',
                transition: 'all 0.2s ease'
              }}
              onClick={() => handleStarClick(starNumber)}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'scale(1.15)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'scale(1)';
              }}
            />
          );
        })}
      </div>
    </div>

    {/* Attendees */}
    {event.max_attendees && (
      <div style={{
        color: theme?.textSecondary || '#64748B',
        fontSize: '14px',
        marginTop: '8px'
      }}>
        {t('attendees')}: {event.current_attendees || 0} / {event.max_attendees}
      </div>
    )}
    </div>

    {/* Join/Leave or Edit Event Button */}
    {isAuthor ? (
      <button
        onClick={handleEditEvent}
        style={{
          width: '100%',
          padding: '12px',
          backgroundColor: theme?.primary || '#024B99',
          color: 'white',
          border: 'none',
          borderRadius: '8px',
          fontSize: '14px',
          fontWeight: '700',
          cursor: 'pointer',
          marginBottom: '16px',
          transition: 'all 0.2s ease'
        }}
      >
        {t('events:edit_event')}
      </button>
    ) : (
      <button
        onClick={handleJoinLeaveEvent}
        style={{
          width: '100%',
          padding: '12px',
          backgroundColor: isAttending ? '#dc2626' : (theme?.primary || '#024B99'),
          color: 'white',
          border: 'none',
          borderRadius: '8px',
          fontSize: '14px',
          fontWeight: '700',
          cursor: 'pointer',
          marginBottom: '16px',
          transition: 'all 0.2s ease'
        }}
      >
        {isAttending ? t('events:leave_event') : t('events:join_event')}
      </button>
    )}

    {/* Contact Information */}
    {(event.contact_phone || event.contact_email) && (
      <div style={{ marginBottom: '24px' }}>
      <h3 style={{
        fontSize: '18px',
        fontWeight: '600',
        color: theme?.text || '#1E293B',
        marginBottom: '12px',
        fontFamily: 'OpenSauceSans, -apple-system, BlinkMacSystemFont, sans-serif'
      }}>
      {t('contact_information')}
      </h3>

      {event.contact_phone && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          marginBottom: '8px',
          color: theme?.textSecondary || '#64748B',
          fontSize: '14px'
        }}>
        <Phone size={16} style={{ marginRight: '8px' }} />
        {event.contact_phone}
        </div>
      )}

      {event.contact_email && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          color: theme?.textSecondary || '#64748B',
          fontSize: '14px'
        }}>
        <Mail size={16} style={{ marginRight: '8px' }} />
        {event.contact_email}
        </div>
      )}
      </div>
    )}

    {/* Report Event link */}
    <div style={{ marginBottom: '16px' }}>
      <button
      onClick={() => {
        if (!currentUserId) {
          setModal({
            visible: true,
            message: t('auth:login_required') || 'Please log in to report content',
            onOk: () => setModal({ visible: false, message: '', onOk: null })
          });
          return;
        }
        setShowReportModal(true);
      }}
      style={{
        background: 'none',
        border: 'none',
        color: '#EF4444',
        fontSize: '12px',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        gap: '5px',
        padding: '0',
        opacity: 0.7
      }}
      >
        <Flag size={12} />
        {t('report_this_event')}
      </button>
    </div>

    {/* Buttons Row - Share and See Their Other Events */}
    <div style={{ display: 'flex', gap: '10px' }}>
      {/* Share Button */}
      <button
      onClick={() => setShowShareModal(true)}
      style={{
        flex: 1,
        padding: '10px',
        background: theme?.primary || '#024B99',
        color: 'white',
        border: 'none',
        borderRadius: '6px',
        fontSize: '13px',
        fontWeight: '600',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '6px'
      }}
      >
      <Share2 size={16} />
      {t('share')}
      </button>

      {/* See Their Other Events Button */}
      <button
      style={{
        flex: 1,
        padding: '10px',
        background: 'white',
        color: theme?.primary || '#024B99',
        border: `2px solid ${theme?.primary || '#024B99'}`,
        borderRadius: '6px',
        fontSize: '12px',
        fontWeight: '600',
        cursor: 'pointer',
        textAlign: 'center'
      }}
      >
      {t('see_their_other_events')}
      </button>
    </div>
    </div>
    </div>

    {/* Main Content Area - Article Content: vertical scrolling, matching NewsDetail's
        TEXT ARTICLE container (LAYOUT.md § Article Content Area: vertical scrolling,
        max-width 600px centred, 24px padding). */}
    <div style={{
      flex: '1',
      position: 'relative',
      height: '100%',
      overflow: 'hidden'
    }}>
    <div
    style={{
      overflowY: 'auto',
      overflowX: 'hidden',
      height: '100%',
      padding: '24px',
      scrollBehavior: 'smooth'
    }}
    >
    <div style={{ maxWidth: '600px', margin: '0 auto', fontSize: '14px', color: '#374151', lineHeight: '1.6' }}>
      {event.content && event.content.trim() !== '' ? (
        formatTextWithStyles(event.content, 'evt-desk-', { marginBottom: '12px' })
      ) : (
        <p style={{ fontStyle: 'italic', color: theme?.textSecondary || '#64748B' }}>
          {t('event_details_coming_soon')}
        </p>
      )}
    </div>
    </div>
    </div>

    {/* Right Sidebar - Comments Section (300px). Same pinned-header/scrolling-body fix as
        ArticleDetail — the heading must not share a scroll region with ConcertinaThread. */}
    <aside className="w-[300px] border-l border-gray-200" style={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      {/* Comments title — flexShrink:0, pinned above the scrolling body */}
      <div style={{ flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative', padding: '12px 12px 0' }}>
        <h3 style={{ fontSize: '15px', fontWeight: 'bold', color: '#1F2937', margin: 0 }}>{t('comments')}</h3>
      </div>

      <div style={{ flex: 1, minHeight: 0, overflow: 'hidden', padding: '12px' }}>
        <ConcertinaThread
          variant="comment"
          data={topLevelComments}
          loadResponses={loadResponses}
          onCreateTopLevel={onCreateTopLevel}
          onCreateReply={onCreateReply}
          onRefresh={reloadComments}
          reportContentType="chat"
          emptyState={t('no_comments_yet')}
        />
      </div>
    </aside>
    </div>

    {/* Footer */}
    <FooterContainer viewType="feed">
      <FooterButtonsDetail />
    </FooterContainer>

    {/* Confirmation Modal */}
    <ConfirmationModal
      visible={modal.visible}
      title={t('notification')}
      message={modal.message}
      onOk={() => {
        try {
          modal.onOk && modal.onOk();
        } catch (e) {
          console.error(e);
          setModal({ visible: false, message: '', onOk: null });
        }
      }}
      onCancel={() => {
        setModal({ visible: false, message: '', onOk: null });
      }}
    />

    {/* Share Link Modal */}
    <ShareLinkModal
      visible={showShareModal}
      targetType="event"
      targetId={event.id}
      targetName={event.title}
      currentUser={userId ? { id: userId } : null}
      context={context}
      isPublic={event.is_public}
      communityId={event.community_id}
      onClose={() => setShowShareModal(false)}
    />

    {/* Report Event Modal */}
    <ReportModal
      visible={showReportModal}
      contentType="event"
      contentId={event.id}
      onClose={() => setShowReportModal(false)}
      onSubmit={async (reportData) => {
        const result = await userActionService.submitReport(currentUserId, reportData);
        if (result.success) {
          setModal({
            visible: true,
            message: t('common:report_submitted') || 'Report submitted. Thank you.',
            onOk: () => setModal({ visible: false, message: '', onOk: null })
          });
        }
      }}
    />
    </div>
  );
};

export default EventsDetail;
